import { SelectOption } from "@magnetic/select/dist/types/types";

import { uuid } from "src/core/globals";
import { SchemaType } from "src/gen/schema/schema_path/schema_path";
import {
  BodyFilter,
  FilterOp,
  filterOpFromJSON,
  IndexFilter,
  ObjectFilter
} from "src/gen/schema/syncfollow/syncfollow";
import { Constants } from "src/utils/constants";
import { camelToSnakeCase } from "src/utils/string";
import { unreachable } from "src/utils/switch";

export interface QueryFilterConfig {
  readonly headingLabel?: string;
  readonly addFilterButtonLabel?: string;
  readonly resetFilterButtonLabel?: string;
  readonly openFilterPanelButtonLabel?: string;
  readonly closeFilterPanelButtonLabel?: string;
  readonly hideAdvanceFilters?: boolean;
}

export interface SearchableField {
  field: string;
  label: string;
  filterType: FilterType;
  indexFilterSchemaType?: SchemaType;
  readonly textFilterConfig?: TextFilterConfig;
  readonly selectFilterConfig?: SelectFilterConfig;
  readonly isDefault?: boolean;
  readonly tooltip?: string;
  readonly hide?: boolean;
}

export interface TextFilterConfig {
  readonly fieldSelectLabel?: string;
  readonly filterValueLabel?: string;
  readonly operatorSelectLabel?: string;
  readonly operatorSelectOptions?: OperatorSelectOptions[];
  readonly isSingleSelect?: boolean;
}

export interface SelectFilterConfig {
  selectOptions: SelectOption[];
}

export interface QueryFilterForm {
  filterCriteria: FilterCriteria[];
}

export interface FilterCriteria {
  id: string;
  filterType: FilterType;
  text?: TextFilterCriteria;
  select?: SelectFilterCriteria;
  multiSelect?: MultiSelectFilterCriteria;
}

export interface TextFilterCriteria {
  fields?: SearchableField[];
  operator?: FilterOp;
  filterValue?: string;
}

export interface SelectFilterCriteria {
  field?: SearchableField;
  operator?: FilterOp;
  filterValue?: string;
}

export interface MultiSelectFilterCriteria {
  field?: SearchableField;
  operator?: FilterOp;
  filterValues?: string[];
}

export enum FilterType {
  TEXT = "text",
  SELECT = "select",
  MULTI_SELECT = "multi-select"
}

export interface OperatorSelectOptions {
  operator: FilterOp;
  label: string;
  notation: string;
}

export function generateDefaultForm(
  searchableFields: SearchableField[]
): QueryFilterForm {
  const filteredFields = searchableFields.filter(
    (searchableField) => !searchableField.hide
  );
  //Generate Select Search Criteria.
  const selectFilterCriteria: FilterCriteria[] = filteredFields
    ?.filter(
      (searchableField) => searchableField.filterType === FilterType.SELECT
    )
    .map((searchableField) => {
      return {
        id: uuid(),
        filterType: FilterType.SELECT,
        select: {
          field: searchableField,
          operator: FilterOp.EQ,
          filterValue: ""
        }
      };
    });

  //Generate Multi-Select Search Criteria.
  const multiSelectFilterCriteria: FilterCriteria[] = filteredFields
    ?.filter(
      (searchableField) =>
        searchableField.filterType === FilterType.MULTI_SELECT
    )
    .map((searchableField) => {
      return {
        id: uuid(),
        filterType: FilterType.MULTI_SELECT,
        multiSelect: {
          field: searchableField,
          operator: FilterOp.EQ,
          filterValues: []
        }
      };
    });

  //Generate Text Filter Criteria.
  const textFields = filteredFields?.filter(
    (searchableField) => searchableField.filterType === FilterType.TEXT
  );
  const isSelect = textFields.some(
    (field) => field.textFilterConfig?.isSingleSelect
  );
  const textFilterCriteria: FilterCriteria[] = [
    {
      id: uuid(),
      filterType: FilterType.TEXT,
      text: {
        fields: isSelect ? textFields.slice(0, 1) : textFields,
        operator: FilterOp.STARTS_WITH,
        filterValue: ""
      }
    }
  ];

  return {
    filterCriteria: selectFilterCriteria
      .concat(multiSelectFilterCriteria)
      .concat(textFilterCriteria)
  };
}

function getOperatorSelectOption(
  operator: FilterOp,
  label: string,
  notation: string
): OperatorSelectOptions {
  return { operator: operator, label: label, notation: notation };
}

function getOperatorSelectOptions(filterOp: FilterOp): OperatorSelectOptions {
  switch (filterOp) {
    case FilterOp.EQ:
      return getOperatorSelectOption(filterOp, "Equals (=)", "=");
    case FilterOp.NOT_EQ:
      return getOperatorSelectOption(filterOp, "Not equals (!=)", "!=");
    case FilterOp.STARTS_WITH:
      return getOperatorSelectOption(filterOp, "Starts with", "STARTS WITH");
    case FilterOp.ENDS_WITH:
      return getOperatorSelectOption(filterOp, "Ends with", "ENDS WITH");
    case FilterOp.GT:
      return getOperatorSelectOption(filterOp, "Greater than (>)", ">");
    case FilterOp.GT_EQ:
      return getOperatorSelectOption(
        filterOp,
        "Greater than or equals (>=)",
        ">="
      );
    case FilterOp.LT:
      return getOperatorSelectOption(filterOp, "Less than (<)", "<");
    case FilterOp.LT_EQ:
      return getOperatorSelectOption(
        filterOp,
        "Less than or equals (<=)",
        "<="
      );
    case FilterOp.CONTAINS:
      return getOperatorSelectOption(filterOp, "Contains", "CONTAINS");
    case FilterOp.UNSPECIFIED:
    case FilterOp.UNRECOGNIZED:
      return getOperatorSelectOption(
        filterOp,
        Constants.EMPTY_VALUE,
        Constants.EMPTY_VALUE
      );
    default:
      unreachable(filterOp);
      return getOperatorSelectOption(
        filterOp,
        Constants.EMPTY_VALUE,
        Constants.EMPTY_VALUE
      );
  }
}

export function generateOperatorSelectOptions(): OperatorSelectOptions[] {
  return Object.keys(FilterOp)
    .filter((key) => isNaN(Number(key)))
    .map((key) => filterOpFromJSON(key))
    .filter((filterOp) => filterOp !== FilterOp.UNRECOGNIZED)
    .filter((filterOp) => filterOp !== FilterOp.UNSPECIFIED)
    .map((filterOp) => {
      return getOperatorSelectOptions(filterOp);
    });
}

export function isValidTextFilterCriteria(filterCriteria: FilterCriteria) {
  return (
    filterCriteria.filterType === FilterType.TEXT &&
    filterCriteria.text &&
    filterCriteria.text.fields &&
    filterCriteria.text.fields.length > 0 &&
    filterCriteria.text.operator &&
    filterCriteria.text.filterValue
  );
}

export function isValidSelectFilterCriteria(filterCriteria: FilterCriteria) {
  return (
    filterCriteria.filterType === FilterType.SELECT &&
    filterCriteria.select &&
    filterCriteria.select.filterValue
  );
}

export function isValidMultiSelectFilterCriteria(
  filterCriteria: FilterCriteria
) {
  return (
    filterCriteria.filterType === FilterType.MULTI_SELECT &&
    filterCriteria.multiSelect &&
    filterCriteria.multiSelect.field &&
    filterCriteria.multiSelect.filterValues &&
    filterCriteria.multiSelect.filterValues.length > 0
  );
}

const createIndexFilter = (
  index?: SchemaType,
  filterOp?: FilterOp,
  filterVal?: string
): IndexFilter => {
  return {
    index: index || SchemaType.NullType,
    filterOp: filterOp || FilterOp.UNSPECIFIED,
    filterVal: filterVal || ""
  };
};

const createBodyFilter = (
  field?: string,
  filterOp?: FilterOp,
  filterVal?: string
): BodyFilter => {
  return {
    field: camelToSnakeCase(field || ""),
    filterOp: filterOp || FilterOp.UNSPECIFIED,
    filterVal: filterVal || ""
  };
};

const createObjectFilter = (
  indexFilters: IndexFilter[],
  bodyFilters: BodyFilter[]
): ObjectFilter => {
  return {
    indexFilters: indexFilters,
    types: [],
    bodyFilters: bodyFilters,
    includeFields: []
  };
};

const createIndexFilterMap = (
  filterCriterias: FilterCriteria[]
): Map<string, IndexFilter[]> => {
  const indexFilters: Map<string, IndexFilter[]> = new Map<
    string,
    IndexFilter[]
  >();

  const addToFilterMap = (
    filterCriteria: FilterCriteria,
    index?: SchemaType,
    filterOp?: FilterOp,
    filterVal?: string
  ) => {
    const existingFilters = indexFilters.get(filterCriteria.id) || [];
    indexFilters.set(
      filterCriteria.id,
      existingFilters.concat(createIndexFilter(index, filterOp, filterVal))
    );
  };

  filterCriterias.forEach((filterCriteria) => {
    //Text Filters
    if (isValidTextFilterCriteria(filterCriteria)) {
      filterCriteria.text?.fields?.forEach((field) => {
        if (field.indexFilterSchemaType) {
          addToFilterMap(
            filterCriteria,
            field.indexFilterSchemaType,
            filterCriteria.text?.operator,
            filterCriteria.text?.filterValue
          );
        }
      });
    }

    //Multi-Select Filters
    if (
      isValidMultiSelectFilterCriteria(filterCriteria) &&
      filterCriteria.multiSelect?.field?.indexFilterSchemaType
    ) {
      filterCriteria.multiSelect?.filterValues?.forEach((filterValue) => {
        addToFilterMap(
          filterCriteria,
          filterCriteria.multiSelect?.field?.indexFilterSchemaType,
          filterCriteria.multiSelect?.operator,
          filterValue
        );
      });
    }

    //Select Filters
    if (
      isValidSelectFilterCriteria(filterCriteria) &&
      filterCriteria.select?.field?.indexFilterSchemaType
    ) {
      addToFilterMap(
        filterCriteria,
        filterCriteria.select?.field?.indexFilterSchemaType,
        filterCriteria.select?.operator,
        filterCriteria.select?.filterValue
      );
    }
  });

  return indexFilters;
};

const createBodyFilterMap = (
  filterCriterias: FilterCriteria[]
): Map<string, BodyFilter[]> => {
  const bodyFilters: Map<string, BodyFilter[]> = new Map<
    string,
    BodyFilter[]
  >();

  const addToFilterMap = (
    filterCriteria: FilterCriteria,
    field?: string,
    filterOp?: FilterOp,
    filterVal?: string
  ) => {
    const existingFilters = bodyFilters.get(filterCriteria.id) || [];
    bodyFilters.set(
      filterCriteria.id,
      existingFilters.concat(createBodyFilter(field, filterOp, filterVal))
    );
  };

  filterCriterias.forEach((filterCriteria) => {
    //Text Filters
    if (isValidTextFilterCriteria(filterCriteria)) {
      filterCriteria.text?.fields?.forEach((field) => {
        if (!field.indexFilterSchemaType) {
          addToFilterMap(
            filterCriteria,
            field.field,
            filterCriteria.text?.operator,
            filterCriteria.text?.filterValue
          );
        }
      });
    }

    //Multi-Select Filters
    if (
      isValidMultiSelectFilterCriteria(filterCriteria) &&
      !filterCriteria.multiSelect?.field?.indexFilterSchemaType
    ) {
      filterCriteria.multiSelect?.filterValues?.forEach((filterValue) => {
        addToFilterMap(
          filterCriteria,
          filterCriteria.multiSelect?.field?.field,
          filterCriteria.multiSelect?.operator,
          filterValue
        );
      });
    }

    //Select Filters
    if (
      isValidSelectFilterCriteria(filterCriteria) &&
      !filterCriteria.select?.field?.indexFilterSchemaType
    ) {
      addToFilterMap(
        filterCriteria,
        filterCriteria.select?.field?.field,
        filterCriteria.select?.operator,
        filterCriteria.select?.filterValue
      );
    }
  });

  return bodyFilters;
};

const createApplyToAllFilters = <F,>(filterMap: Map<string, F[]>): F[] => {
  const filters: F[] = [];
  filterMap.forEach((filterArray: F[]) => {
    if (filterArray.length === 1) {
      filters.push(...filterArray);
    }
  });
  return filters;
};

const getORConditionFilters = <F,>(filterMap: Map<string, F[]>): F[][] => {
  const orConditionFilters: F[][] = [];
  filterMap.forEach((filterArray: F[]) => {
    if (filterArray.length > 1) {
      orConditionFilters.push(filterArray);
    }
  });
  return orConditionFilters;
};

/**
 * Creates the combination of filters that needs to be OR'ed together..
 *
 * Code with sample input and output can be executed in the TS Playground link below:
 * https://tsplay.dev/filter-combination-sample
 *
 * @param orConditionFilters
 * @param combinationValidator
 */
const getFilterCombination = <F,>(
  orConditionFilters: F[][],
  combinationValidator?: (filter: F, combo: F[]) => boolean
): F[][] => {
  const getCombinations = (index: number): F[][] => {
    if (orConditionFilters.length === 0) {
      return [];
    }
    if (index === orConditionFilters.length) {
      return [[]];
    }

    const result: F[][] = [];
    const currentFilter = orConditionFilters[index];
    const combinationFilter = getCombinations(index + 1);

    currentFilter?.forEach((filter) => {
      for (const comboFilter of combinationFilter) {
        if (combinationValidator) {
          if (combinationValidator(filter, comboFilter)) {
            result.push([filter, ...comboFilter]);
          }
        } else {
          result.push([filter, ...comboFilter]);
        }
      }
    });

    return result;
  };
  return getCombinations(0);
};

/**
 * Creates an array of Object Filters using generic/apply to all index and body filters and index & body filter
 * combinations.
 *
 * Any of the below params can be empty but since we need to double loop over them and ensure we don't miss out
 * on a possible scenario - mock the param as null (if needed) so that loop is executed at least once.
 *
 * @param applyToAllIndexFilters
 * @param applyToAllBodyFilters
 * @param indexFilterCombinations
 * @param bodyFilterCombinations
 */
const createObjectFilters = (
  applyToAllIndexFilters: IndexFilter[],
  applyToAllBodyFilters: BodyFilter[],
  indexFilterCombinations: IndexFilter[][],
  bodyFilterCombinations: BodyFilter[][]
) => {
  const objectFilters: ObjectFilter[] = [];
  const outerLoopFilters = indexFilterCombinations.length
    ? indexFilterCombinations
    : [null];
  const innerLoopFilters = bodyFilterCombinations.length
    ? bodyFilterCombinations
    : [null];

  for (const oFilter of outerLoopFilters) {
    for (const iFilter of innerLoopFilters) {
      const indexFilters: IndexFilter[] = [];
      const bodyFilters: BodyFilter[] = [];

      if (oFilter !== null) {
        indexFilters.push(...oFilter);
      }
      indexFilters.push(...applyToAllIndexFilters);

      if (iFilter !== null) {
        bodyFilters.push(...iFilter);
      }
      bodyFilters.push(...applyToAllBodyFilters);

      if (indexFilters.length > 0 || bodyFilters.length > 0) {
        objectFilters.push(createObjectFilter(indexFilters, bodyFilters));
      }
    }
  }
  return objectFilters;
};

/**
 * Creates Object Filters based on the input filter criteria.
 * Applies the static index and body filters to all the ObjectFilters.
 *
 * Refer to queryFilter.md for additional details..
 *
 * @param filterCriterias
 * @param staticIndexFilters
 * @param staticBodyFilters
 * @param combinationValidator
 * @returns An array of Object Filters to be applied.
 */
export const getObjectFilters = (
  filterCriterias: FilterCriteria[],
  staticIndexFilters: IndexFilter[],
  staticBodyFilters: BodyFilter[],
  combinationValidator?: (filter: BodyFilter, combo: BodyFilter[]) => boolean
): ObjectFilter[] => {
  const indexFilterMap = createIndexFilterMap(filterCriterias);
  const bodyFilterMap = createBodyFilterMap(filterCriterias);

  return createObjectFilters(
    createApplyToAllFilters(indexFilterMap).concat(staticIndexFilters),
    createApplyToAllFilters(bodyFilterMap).concat(staticBodyFilters),
    getFilterCombination(getORConditionFilters(indexFilterMap)),
    getFilterCombination(
      getORConditionFilters(bodyFilterMap),
      combinationValidator
    )
  );
};

export const generateInitialDefaultForm = (
  searchableFields: SearchableField[]
): QueryFilterForm => {
  const defaultSearchableFields = searchableFields.filter(
    (field: SearchableField) => field.isDefault
  );
  return generateDefaultForm(defaultSearchableFields);
};
